<template>
  <section class="rounded-[24px] border border-[#eee4da] bg-white p-4 shadow-sm sm:p-6">
    <div class="flex flex-col gap-3 border-b border-[#eee4da] pb-5 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <RouterLink to="/dashboard/settings" class="inline-flex items-center gap-2 text-sm font-semibold text-brand">
          <i class="ri-arrow-left-line"></i>
          Settings
        </RouterLink>
        <h2 class="mt-3 text-[1.65rem] font-semibold leading-tight text-ink">Change Password</h2>
        <p class="mt-1 text-sm text-muted">Use a strong password to keep your vendor account protected.</p>
      </div>
      <span class="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fbf1e6] text-2xl text-brand">
        <i class="ri-lock-password-line"></i>
      </span>
    </div>

    <form class="mt-6 max-w-2xl space-y-4" @submit.prevent="changePassword">
      <label v-for="field in passwordFields" :key="field.key" class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
        {{ field.label }} <span class="text-brand">*</span>
        <div class="mt-2 flex rounded-2xl border border-[#e8ddd1] focus-within:border-brand">
          <input v-model="form[field.key]" :type="visible[field.key] ? 'text' : 'password'" class="w-full rounded-2xl px-4 py-3 text-sm normal-case font-normal text-ink focus:outline-none">
          <button v-if="field.canGenerate" type="button" class="border-l border-[#f0e5da] px-3 text-lg text-muted transition hover:text-brand" title="Generate password" @click="generatePassword">
            <i class="ri-magic-line"></i>
          </button>
          <button type="button" class="border-l border-[#f0e5da] px-4 text-lg text-muted transition hover:text-brand" @click="visible[field.key] = !visible[field.key]">
            <i :class="visible[field.key] ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
          </button>
        </div>
        <p v-if="errors[field.key]" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ errors[field.key] }}</p>
      </label>

      <div class="rounded-2xl border border-[#eadfd4] bg-[#fcf7f2] p-4">
        <div class="flex items-center justify-between gap-3 text-sm">
          <span class="font-semibold text-ink">Password strength</span>
          <span class="font-semibold" :class="passwordStrengthTextClass">{{ passwordStrengthLabel }}</span>
        </div>
        <div class="mt-3 h-2 overflow-hidden rounded-full bg-white">
          <div class="h-full rounded-full transition-all" :class="passwordStrengthBarClass" :style="{ width: `${passwordStrengthPercent}%` }"></div>
        </div>
        <div class="mt-4 grid gap-2 text-sm text-muted sm:grid-cols-2">
          <p v-for="rule in passwordRuleList" :key="rule.key" class="flex items-center gap-2" :class="rule.valid ? 'text-emerald-700' : 'text-muted'">
            <i :class="rule.valid ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'"></i>
            <span>{{ rule.label }}</span>
          </p>
        </div>
      </div>

      <p v-if="formError" class="text-sm font-semibold text-red-600">{{ formError }}</p>

      <div class="flex flex-col gap-3 sm:flex-row sm:justify-end">
        <RouterLink to="/dashboard/settings" class="inline-flex items-center justify-center rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand">
          Cancel
        </RouterLink>
        <button type="submit" class="inline-flex items-center justify-center rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:opacity-60" :disabled="saving">
          {{ saving ? 'Updating...' : 'Update password' }}
        </button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { RouterLink } from 'vue-router'
import Swal from 'sweetalert2'
import api from '@/services/vendor/api'
import { getAuthUserId } from '@/services/auth'

const saving = ref(false)
const formError = ref('')

const form = reactive({
  oldpwd: '',
  newpwd: '',
  confirm: ''
})

const visible = reactive({
  oldpwd: false,
  newpwd: false,
  confirm: false
})

const errors = reactive({
  oldpwd: '',
  newpwd: '',
  confirm: ''
})

const passwordFields = [
  { key: 'oldpwd', label: 'Current password' },
  { key: 'newpwd', label: 'New password', canGenerate: true },
  { key: 'confirm', label: 'Confirm new password' }
]

const passwordRules = computed(() => ({
  minLength: form.newpwd.length >= 8,
  uppercase: /[A-Z]/.test(form.newpwd),
  lowercase: /[a-z]/.test(form.newpwd),
  number: /\d/.test(form.newpwd),
  special: /[^A-Za-z0-9]/.test(form.newpwd)
}))

const passwordRuleList = computed(() => [
  { key: 'minLength', label: 'At least 8 characters', valid: passwordRules.value.minLength },
  { key: 'uppercase', label: 'Contains uppercase letter', valid: passwordRules.value.uppercase },
  { key: 'lowercase', label: 'Contains lowercase letter', valid: passwordRules.value.lowercase },
  { key: 'number', label: 'Contains number', valid: passwordRules.value.number },
  { key: 'special', label: 'Contains special character', valid: passwordRules.value.special }
])

const passwordStrengthScore = computed(() => Object.values(passwordRules.value).filter(Boolean).length)
const passwordStrengthPercent = computed(() => passwordStrengthScore.value * 20)
const passwordStrengthLabel = computed(() => {
  if (!form.newpwd) return 'Enter password'
  if (passwordStrengthScore.value <= 2) return 'Weak'
  if (passwordStrengthScore.value <= 4) return 'Medium'
  return 'Strong'
})
const passwordStrengthBarClass = computed(() => {
  if (!form.newpwd) return 'bg-[#e8ddd1]'
  if (passwordStrengthScore.value <= 2) return 'bg-red-500'
  if (passwordStrengthScore.value <= 4) return 'bg-amber-500'
  return 'bg-emerald-600'
})
const passwordStrengthTextClass = computed(() => {
  if (!form.newpwd) return 'text-muted'
  if (passwordStrengthScore.value <= 2) return 'text-red-600'
  if (passwordStrengthScore.value <= 4) return 'text-amber-600'
  return 'text-emerald-700'
})

const validatePasswordStrength = () => passwordStrengthScore.value === 5

const generatePassword = () => {
  const groups = [
    'ABCDEFGHJKLMNPQRSTUVWXYZ',
    'abcdefghijkmnopqrstuvwxyz',
    '23456789',
    '!@#$%^&*?'
  ]
  const allChars = groups.join('')
  const result = groups.map((group) => group[Math.floor(Math.random() * group.length)])
  while (result.length < 14) {
    result.push(allChars[Math.floor(Math.random() * allChars.length)])
  }
  form.newpwd = result.sort(() => Math.random() - 0.5).join('')
  form.confirm = form.newpwd
  visible.newpwd = true
  visible.confirm = true
}

const validate = () => {
  errors.oldpwd = form.oldpwd ? '' : 'Current password is required'
  errors.newpwd = validatePasswordStrength() ? '' : 'Use a stronger password'
  errors.confirm = form.confirm === form.newpwd ? '' : 'Passwords do not match'
  return !errors.oldpwd && !errors.newpwd && !errors.confirm
}

const resetForm = () => {
  form.oldpwd = ''
  form.newpwd = ''
  form.confirm = ''
  visible.oldpwd = false
  visible.newpwd = false
  visible.confirm = false
}

const changePassword = async () => {
  formError.value = ''
  if (!validate()) return
  const vendorId = getAuthUserId()
  if (!vendorId) {
    formError.value = 'Vendor id is missing. Sign in again.'
    return
  }

  saving.value = true
  try {
    const fd = new FormData()
    fd.append('id', vendorId)
    fd.append('vendor_id', vendorId)
    fd.append('oldpwd', form.oldpwd)
    fd.append('newpwd', form.newpwd)
    fd.append('old_password', form.oldpwd)
    fd.append('new_password', form.newpwd)
    const { data } = await api.changePasswordVendor(fd)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) throw new Error(data?.msg || data?.message || 'Failed to change password')
    resetForm()
    await Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Password changed', showConfirmButton: false, timer: 1800 })
  } catch (error) {
    formError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to change password'
  } finally {
    saving.value = false
  }
}
</script>
